import { filter, map, merge, withLatestFrom } from 'rxjs'
import type { Epic } from '../../index.js'
import { authenticationSlice } from '../../Slice/Authentication/AuthenticationSlice.js'
import type { IWebsocketConfig } from '../../Slice/Application/ApplicationSlice.js'
import { connectToServer, connectivityProbeSucceeded } from '../../Slice/Application/ApplicationSlice.js'

/*
 * Re-arms the websocket once the reason it died has gone away.
 *
 * connectToServerEpic gives up after a bounded number of attempts on purpose — an
 * unbounded retry loop is what made a dead backend hammer every open tab. Bounded
 * give-up is only correct if someone re-arms it, and that is this epic. It emits
 * connectToServer (not reconnectToServer) so the whole connection machinery is
 * rebuilt, which is also what resets the attempt counter.
 *
 * Two independent recoveries, matching the two ways the socket can die for good:
 *
 * - Outage: the connectivity layer probes /health only while offline, so a
 *   successful probe always means "we were down, we are back". No state guard is
 *   needed, and connectionReady deliberately does not emit that action — a socket
 *   announcing its own open would re-arm itself forever.
 * - Rejected credentials: close code 3000 terminates the socket without a
 *   reconnect (correct — retrying with the same bad token is pointless), and
 *   nothing used to bring it back even after the token was refreshed. Guarded on
 *   the flag so an ordinary token refresh on a healthy socket changes nothing.
 */
export const reconnectWebsocketEpic: Epic = (action$, state$) =>
  merge(
    action$.pipe(filter(connectivityProbeSucceeded.match), withLatestFrom(state$)),
    action$.pipe(
      filter(authenticationSlice.actions.fetchUserIfPresentSuccess.match),
      withLatestFrom(state$),
      /* Only worth retrying if we actually have a user now — fetchUserIfPresentSuccess
       * also fires with no user on public pages, and reconnecting there would just
       * earn another 3000. */
      filter(([, state]) => state.application.websocketUnauthorized && state.authentication.user !== undefined)
    )
  ).pipe(
    map(([, state]) => state.application.websocketConfig),
    /* No config yet means no socket was ever asked for — nothing to re-arm. */
    filter((config): config is IWebsocketConfig => config !== undefined),
    map((config) => connectToServer(config))
  )
